import React, { useState, useEffect } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  CalendarDays, Clock, Plus, Users, Trash2, Loader2, Edit, Copy, Bell, Repeat, Search, AlertTriangle, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { formatApiError } from '../lib/apiError';
import { formatActivity } from '../lib/formatActivity';

export const StudioClassesPage = () => {
  const { api } = useAuth();
  const [studio, setStudio] = useState(null);
  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState('future'); // 'future', 'past', 'all'

  // Dialog states
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [showEditClass, setShowEditClass] = useState(false);
  const [showCloneClass, setShowCloneClass] = useState(false);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  // Iter-46ag: shown after submitting edit on a recurring class — asks
  // "this class only / this and all future classes". Holds the payload
  // we'll send so the user can confirm scope before we PUT.
  const [recurringScopeDialog, setRecurringScopeDialog] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [classForm, setClassForm] = useState({
    name: '', description: '', start_time: '', end_time: '', max_capacity: 20,
    price: 0, activity_type: '', instructor_id: '', color: '',
    recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [recurrenceDatePickerOpen, setRecurrenceDatePickerOpen] = useState(false);

  // Predefined colors for class cards (with transparency)
  const classColors = [
    { name: 'Default', value: '', bg: 'bg-card' },
    { name: 'Blue', value: 'blue', bg: 'bg-blue-100/50' },
    { name: 'Green', value: 'green', bg: 'bg-green-100/50' },
    { name: 'Purple', value: 'purple', bg: 'bg-purple-100/50' },
    { name: 'Orange', value: 'orange', bg: 'bg-orange-100/50' },
    { name: 'Pink', value: 'pink', bg: 'bg-pink-100/50' },
    { name: 'Teal', value: 'teal', bg: 'bg-teal-100/50' },
    { name: 'Yellow', value: 'yellow', bg: 'bg-yellow-100/50' },
    { name: 'Red', value: 'red', bg: 'bg-red-100/50' },
    { name: 'Indigo', value: 'indigo', bg: 'bg-indigo-100/50' },
  ];

  const getColorClass = (color) => {
    const found = classColors.find(c => c.value === color);
    return found ? found.bg : 'bg-card';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studioRes, instructorsRes] = await Promise.all([
        apiGet('/api/studios/me'),
        apiGet('/api/instructors').catch(() => [])
      ]);
      setStudio(studioRes);
      setActivityTypes(studioRes.activity_types || []);
      setInstructors(instructorsRes || []);
      
      // Fetch classes separately to handle "no classes" case gracefully
      try {
        const classesRes = await apiGet('/api/studio/dashboard');
        setClasses(classesRes.classes || []);
      } catch (classErr) {
        // No classes or other issue - just show empty state
        setClasses([]);
      }
    } catch (e) {
      console.error('Failed to load data:', e);
      // Don't show error toast for empty state
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!classForm.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    setFormLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const payload = {
        ...classForm,
        studio_id: studio.id,
        category: classForm.activity_type,
        start_time: `${dateStr}T${classForm.start_time}:00`,
        end_time: `${dateStr}T${classForm.end_time}:00`,
        instructor_id: classForm.instructor_id || null,
        recurrence_end_date: recurrenceEndDate ? format(recurrenceEndDate, "yyyy-MM-dd'T'23:59:59") : null
      };
      
      if (classForm.recurring && classForm.recurrence_rule) {
        await apiPost('/api/classes/recurring', { body: payload });
      } else {
        await apiPost('/api/classes', { body: payload });
      }
      
      setShowCreateClass(false);
      resetForm();
      toast.success('Class created!');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const payload = {
      name: classForm.name,
      description: classForm.description,
      start_time: `${dateStr}T${classForm.start_time}:00`,
      end_time: `${dateStr}T${classForm.end_time}:00`,
      max_capacity: classForm.max_capacity,
      price: classForm.price,
      activity_type: classForm.activity_type,
      instructor_id: classForm.instructor_id || null,
      color: classForm.color || null
    };

    // Iter-46ag: recurring class? Ask the owner whether the edit applies to
    // just this occurrence or every future one in the series. Single
    // classes go straight through.
    if (selectedClass?.recurring) {
      setRecurringScopeDialog({ payload, classId: selectedClass.id });
      return;
    }

    await submitClassEdit(selectedClass.id, payload, /* scope */ 'single');
  };

  const submitClassEdit = async (classId, payload, scope) => {
    setFormLoading(true);
    try {
      if (scope === 'series') {
        await apiPut('/api/classes/{class_id}/series', {
          params: { class_id: classId },
          body: payload,
        });
        toast.success('All future classes updated!');
      } else {
        await apiPut('/api/classes/{class_id}', {
          params: { class_id: classId },
          body: payload,
        });
        toast.success('Class updated!');
      }
      setShowEditClass(false);
      setRecurringScopeDialog(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloneClass = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await apiPost('/api/classes/{class_id}/clone', { params: { class_id: selectedClass.id }, body: {
        ...classForm,
        studio_id: studio.id,
        start_time: `${dateStr}T${classForm.start_time}:00`,
        end_time: `${dateStr}T${classForm.end_time}:00`,
        instructor_id: classForm.instructor_id || null,
        recurrence_end_date: recurrenceEndDate ? format(recurrenceEndDate, "yyyy-MM-dd'T'23:59:59") : null
      } });
      setShowCloneClass(false);
      toast.success('Class(es) created!');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to clone class'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await apiDelete('/api/classes/{class_id}', { params: { class_id: classId } });
      toast.success('Class deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete class');
    }
  };

  const openEditDialog = (cls) => {
    setSelectedClass(cls);
    setClassForm({
      name: cls.name,
      description: cls.description || '',
      start_time: cls.start_time?.split('T')[1]?.substring(0, 5) || '',
      end_time: cls.end_time?.split('T')[1]?.substring(0, 5) || '',
      max_capacity: cls.max_capacity,
      price: cls.price,
      activity_type: cls.activity_type || '',
      instructor_id: cls.instructor_id || '',
      color: cls.color || '',
      recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
    });
    if (cls.start_time) setSelectedDate(new Date(cls.start_time));
    setShowEditClass(true);
  };

  const openCloneDialog = (cls) => {
    setSelectedClass(cls);
    setClassForm({
      name: `${cls.name} (Copy)`,
      description: cls.description || '',
      start_time: cls.start_time?.split('T')[1]?.substring(0, 5) || '',
      end_time: cls.end_time?.split('T')[1]?.substring(0, 5) || '',
      max_capacity: cls.max_capacity,
      price: cls.price,
      activity_type: cls.activity_type || '',
      instructor_id: cls.instructor_id || '',
      color: cls.color || '',
      recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
    });
    setSelectedDate(new Date());
    setRecurrenceEndDate(null);
    setShowCloneClass(true);
  };

  const handleViewWaitlist = async (cls) => {
    setSelectedClass(cls);
    try {
      const data = await apiGet('/api/waitlist/class/{class_id}', { params: { class_id: cls.id } });
      // API returns { entries: [...] }, extract the array
      setWaitlistEntries(data.entries || []);
      setShowWaitlistDialog(true);
    } catch (e) {
      toast.error('Failed to load waitlist');
    }
  };

  const handlePromoteFromWaitlist = async (entryId) => {
    try {
      await apiPost('/api/waitlist/{waitlist_id}/promote', { params: { waitlist_id: entryId } });
      toast.success('Client promoted from waitlist!');
      handleViewWaitlist(selectedClass);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to promote'));
    }
  };

  const resetForm = () => {
    setClassForm({
      name: '', description: '', start_time: '', end_time: '', max_capacity: 20,
      price: 0, activity_type: '', instructor_id: '',
      recurring: false, recurrence_rule: '', recurrence_end_date: '', recurrence_days: []
    });
    setRecurrenceEndDate(null);
  };

  // Filter classes based on search term and time filter
  const filteredClasses = classes.filter(cls => {
    // Time filter
    const now = new Date();
    const classDate = new Date(cls.start_time);
    if (timeFilter === 'future' && classDate < now) return false;
    if (timeFilter === 'past' && classDate >= now) return false;
    
    // Search filter
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      cls.name?.toLowerCase().includes(term) ||
      cls.activity_type?.toLowerCase().includes(term) ||
      cls.instructor_name?.toLowerCase().includes(term) ||
      cls.description?.toLowerCase().includes(term)
    );
  }).sort((a, b) => {
    // Sort: future classes ascending, past classes descending
    const dateA = new Date(a.start_time);
    const dateB = new Date(b.start_time);
    return timeFilter === 'past' ? dateB - dateA : dateA - dateB;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="studio-classes-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Classes</h1>
            <p className="text-muted-foreground">Manage your class schedule and waitlists</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateClass(true); }} data-testid="add-class-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Class
          </Button>
        </div>

        {/* Search and Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Search by name, instructor, or activity type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="class-search"
                />
              </div>
              <Tabs value={timeFilter} onValueChange={setTimeFilter} className="w-full md:w-auto">
                <TabsList>
                  <TabsTrigger value="future" data-testid="filter-future">Upcoming</TabsTrigger>
                  <TabsTrigger value="past" data-testid="filter-past">Past</TabsTrigger>
                  <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardContent>
        </Card>

        {/* Classes List */}
        <Card>
          <CardHeader>
            <CardTitle>
              {timeFilter === 'future' ? 'Upcoming' : timeFilter === 'past' ? 'Past' : 'All'} Classes ({filteredClasses.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredClasses.length > 0 ? (
              <div className="space-y-3">
                {filteredClasses.map((cls, i) => {
                  const isPast = new Date(cls.start_time) < new Date();
                  return (
                    <div
                      key={cls.id}
                      className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border rounded-lg ${isPast ? 'opacity-60 bg-muted/30' : getColorClass(cls.color)}`}
                      data-testid={`class-row-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge variant="outline">{formatActivity(cls.activity_type) || 'General'}</Badge>
                          <h4 className="font-semibold">{cls.name}</h4>
                          {cls.recurring && <Badge variant="secondary"><Repeat className="w-3 h-3 mr-1" />Recurring</Badge>}
                          {isPast && <Badge variant="secondary">Past</Badge>}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarDays className="w-4 h-4" />
                            {cls.start_time ? format(new Date(cls.start_time), 'EEE, MMM d') : 'N/A'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {cls.start_time ? format(new Date(cls.start_time), 'h:mm a') : ''} - {cls.end_time ? format(new Date(cls.end_time), 'h:mm a') : ''}
                          </span>
                          {cls.instructor_name && <span>with {cls.instructor_name}</span>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between md:justify-end gap-3 md:gap-4">
                        <div className="text-right">
                          <p className="font-semibold flex items-center gap-1 md:justify-end">
                            <Users className="w-4 h-4" />
                            {cls.current_bookings || 0}/{cls.max_capacity}
                          </p>
                          {(cls.waitlist_count || 0) > 0 && (
                            <p className="text-xs text-amber-600">{cls.waitlist_count} on waitlist</p>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {!isPast && (cls.waitlist_count || 0) > 0 && (
                            <Button variant="outline" size="sm" onClick={() => handleViewWaitlist(cls)} data-testid={`waitlist-class-${cls.id}`}>
                              <Bell className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(cls)} data-testid={`edit-class-${cls.id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => openCloneDialog(cls)} data-testid={`clone-class-${cls.id}`}><Copy className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteClass(cls.id)} data-testid={`delete-class-${cls.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarDays className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground">No classes scheduled</p>
                <Button className="mt-4" onClick={() => setShowCreateClass(true)}>Create Your First Class</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Class Dialog */}
      <Dialog open={showCreateClass || showEditClass || showCloneClass} onOpenChange={(open) => {
        if (!open) { setShowCreateClass(false); setShowEditClass(false); setShowCloneClass(false); }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {showEditClass ? 'Edit Class' : showCloneClass ? 'Clone Class' : 'Create New Class'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={showEditClass ? handleEditClass : showCloneClass ? handleCloneClass : handleCreateClass} className="space-y-4">
            {/* Show alert if no activity types defined */}
            {activityTypes.length === 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No Activity Types Defined</AlertTitle>
                <AlertDescription>
                  You need to create activity types before adding classes.{' '}
                  <Link to="/studio/settings" className="font-medium underline underline-offset-4 hover:text-primary">
                    Go to Settings → Activity Types
                  </Link>{' '}
                  to define your activity types first.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Class Name *</Label>
              <Input value={classForm.name} onChange={(e) => setClassForm({ ...classForm, name: e.target.value })} required />
            </div>

            <div className="space-y-2">
              <Label>Activity Type *</Label>
              <Select value={classForm.activity_type} onValueChange={(v) => setClassForm({ ...classForm, activity_type: v })}>
                <SelectTrigger className="capitalize"><SelectValue placeholder="Select activity" /></SelectTrigger>
                <SelectContent>
                  {activityTypes.map((type, i) => (
                    <SelectItem key={i} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarDays className="mr-2 h-4 w-4" />
                    {format(selectedDate, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar 
                    mode="single" 
                    selected={selectedDate} 
                    onSelect={(d) => {
                      if (d) {
                        setSelectedDate(d);
                        setDatePickerOpen(false);
                      }
                    }} 
                    initialFocus 
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input type="time" value={classForm.start_time} onChange={(e) => setClassForm({ ...classForm, start_time: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input type="time" value={classForm.end_time} onChange={(e) => setClassForm({ ...classForm, end_time: e.target.value })} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Capacity</Label>
                <Input type="number" value={classForm.max_capacity} onChange={(e) => setClassForm({ ...classForm, max_capacity: parseInt(e.target.value) })} min={1} />
              </div>
              <div className="space-y-2">
                <Label>Price (€)</Label>
                <Input type="number" step="0.01" value={classForm.price} onChange={(e) => setClassForm({ ...classForm, price: parseFloat(e.target.value) })} min={0} />
              </div>
            </div>

            {instructors.length > 0 && (
              <div className="space-y-2">
                <Label>Instructor</Label>
                <Select value={classForm.instructor_id} onValueChange={(v) => setClassForm({ ...classForm, instructor_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select instructor" /></SelectTrigger>
                  <SelectContent>
                    {instructors.map((inst) => (
                      <SelectItem key={inst.id} value={inst.id}>{inst.first_name} {inst.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Recurring options for create/clone */}
            {!showEditClass && (
              <div className="pt-4 border-t space-y-4">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="recurring" checked={classForm.recurring} 
                    onChange={(e) => setClassForm({ ...classForm, recurring: e.target.checked })} className="w-4 h-4" />
                  <label htmlFor="recurring" className="text-sm font-medium"><Repeat className="w-4 h-4 inline mr-1" />Create recurring schedule</label>
                </div>

                {classForm.recurring && (
                  <>
                    <div className="space-y-2">
                      <Label>Recurrence Pattern</Label>
                      <Select value={classForm.recurrence_rule} onValueChange={(v) => setClassForm({ ...classForm, recurrence_rule: v })}>
                        <SelectTrigger><SelectValue placeholder="Select pattern" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {classForm.recurrence_rule === 'custom' && (
                      <div className="flex flex-wrap gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                          <Button key={day} type="button" size="sm"
                            variant={classForm.recurrence_days.includes(i) ? 'default' : 'outline'}
                            onClick={() => {
                              const days = classForm.recurrence_days.includes(i)
                                ? classForm.recurrence_days.filter(d => d !== i)
                                : [...classForm.recurrence_days, i];
                              setClassForm({ ...classForm, recurrence_days: days });
                            }}>{day}</Button>
                        ))}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Popover open={recurrenceDatePickerOpen} onOpenChange={setRecurrenceDatePickerOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarDays className="mr-2 h-4 w-4" />
                            {recurrenceEndDate ? format(recurrenceEndDate, 'PPP') : 'Pick end date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar 
                            mode="single" 
                            selected={recurrenceEndDate} 
                            onSelect={(d) => {
                              setRecurrenceEndDate(d);
                              setRecurrenceDatePickerOpen(false);
                            }} 
                            initialFocus 
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Color Picker */}
            <div className="space-y-2">
              <Label>Card Color (Optional)</Label>
              <div className="flex flex-wrap gap-2">
                {classColors.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setClassForm({ ...classForm, color: color.value })}
                    className={`w-8 h-8 rounded-md border-2 transition-all ${color.bg} ${
                      classForm.color === color.value 
                        ? 'border-primary ring-2 ring-primary ring-offset-2' 
                        : 'border-transparent hover:border-muted-foreground/30'
                    }`}
                    title={color.name}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Select a background color for this class card</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateClass(false); setShowEditClass(false); setShowCloneClass(false); }}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !classForm.activity_type || activityTypes.length === 0}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : showEditClass ? 'Save Changes' : classForm.recurring ? 'Create Classes' : 'Create Class'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Waitlist Dialog */}
      <Dialog open={showWaitlistDialog} onOpenChange={setShowWaitlistDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Waitlist for {selectedClass?.name}</DialogTitle>
            <DialogDescription>{waitlistEntries.length} people waiting</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {waitlistEntries.map((entry, i) => (
              <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">#{entry.position} - {entry.user_name || entry.user_email}</p>
                  <p className="text-xs text-muted-foreground">Joined {format(new Date(entry.joined_at), 'MMM d, h:mm a')}</p>
                </div>
                {entry.status === 'waiting' && (
                  <Button size="sm" onClick={() => handlePromoteFromWaitlist(entry.id)}>Promote</Button>
                )}
              </div>
            ))}
            {waitlistEntries.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No one on the waitlist</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Iter-46ag: Recurring class edit scope dialog */}
      <Dialog open={!!recurringScopeDialog} onOpenChange={(open) => { if (!open) setRecurringScopeDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply changes to which classes?</DialogTitle>
            <DialogDescription>
              This is part of a recurring series. Choose how widely the edit should apply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button
              type="button"
              disabled={formLoading}
              onClick={() => recurringScopeDialog && submitClassEdit(recurringScopeDialog.classId, recurringScopeDialog.payload, 'single')}
              data-testid="edit-scope-single-btn"
              className="w-full text-left p-4 rounded-lg border-2 border-border hover:border-primary/50 transition-all disabled:opacity-50"
            >
              <p className="font-medium">Just this class</p>
              <p className="text-xs text-muted-foreground">
                Only the selected date is changed. The rest of the series stays as-is. Best for one-off instructor swaps when someone calls in sick.
              </p>
            </button>
            <button
              type="button"
              disabled={formLoading}
              onClick={() => recurringScopeDialog && submitClassEdit(recurringScopeDialog.classId, recurringScopeDialog.payload, 'series')}
              data-testid="edit-scope-series-btn"
              className="w-full text-left p-4 rounded-lg border-2 border-border hover:border-primary/50 transition-all disabled:opacity-50"
            >
              <p className="font-medium">This and all future classes</p>
              <p className="text-xs text-muted-foreground">
                Updates this occurrence and every later one in the series. Past classes are never touched. Class date/time is preserved per occurrence.
              </p>
            </button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRecurringScopeDialog(null)} disabled={formLoading}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
